import { useState } from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { founderDashboardAPI } from '../../../../services/api';
import {
  PageHeader,
  PageState,
  StaleNotice,
  StatusBadge,
} from './OutreachShared';
import {
  focusRing,
  formatDateTime,
  humanize,
  listFrom,
  useOutreachResource,
} from './outreachUtils';

const PAGE_SIZE = 25;

export default function OutreachDeliveriesPage() {
  const [offset, setOffset] = useState(0);
  const resource = useOutreachResource(
    () => founderDashboardAPI.getOutreachDeliveries({ limit: PAGE_SIZE, offset }),
    [offset]
  );
  const rows = listFrom(resource.data, 'deliveries', 'items', 'rows');
  const total = Number(resource.data?.pagination?.total ?? resource.data?.total ?? rows.length);

  return (
    <div className="space-y-5">
      <PageHeader
        title="Deliveries"
        description="Provider evidence and durable delivery outcomes. Unknown evidence remains unknown."
        refreshing={resource.refreshing}
        onRefresh={resource.reload}
      />
      <StaleNotice
        generatedAt={resource.data?.generatedAt || resource.data?.generated_at}
        loadedAt={resource.loadedAt}
      />

      <div className="flex gap-3 rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
        <InformationCircleIcon className="h-5 w-5 flex-none" />
        <p>
          This is an evidence view only. It intentionally offers no blind retry action when a
          provider outcome is missing or uncertain.
        </p>
      </div>

      <PageState
        loading={resource.loading}
        error={resource.error}
        permissionDenied={resource.permissionDenied}
        empty={rows.length === 0}
        emptyTitle="No delivery evidence"
        emptyMessage="Delivery records will appear after candidate evaluation creates an intent."
        onRetry={resource.reload}
      >
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-[1050px] w-full text-left text-sm">
              <caption className="sr-only">
                Lifecycle email deliveries with masked identities and provider evidence
              </caption>
              <thead className="bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-3">Outcome</th>
                  <th className="px-4 py-3">Masked identity</th>
                  <th className="px-4 py-3">Campaign</th>
                  <th className="px-4 py-3">Lane</th>
                  <th className="px-4 py-3">Queued</th>
                  <th className="px-4 py-3">Last evidence</th>
                  <th className="px-4 py-3">Provider ID</th>
                  <th className="px-4 py-3">Reason</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((row, index) => {
                  const status =
                    row.status ||
                    row.deliveryStatus ||
                    row.delivery_status ||
                    row.providerOutcome ||
                    row.provider_outcome ||
                    'unknown';
                  const reason =
                    row.reasonCode ||
                    row.reason_code ||
                    row.failureReason ||
                    row.failure_reason ||
                    row.cancelReason ||
                    row.cancel_reason ||
                    row.evidence?.failureClass ||
                    row.evidence?.ambiguityClass ||
                    row.evidence?.skipReason ||
                    row.evidence?.cancelReason;
                  return (
                    <tr key={row.id || row.deliveryId || `${offset}-${index}`} className="align-top">
                      <td className="px-4 py-4"><StatusBadge value={status} /></td>
                      <td className="px-4 py-4 font-medium text-gray-900">
                        {row.maskedIdentity ||
                          row.masked_identity ||
                          row.recipientMasked ||
                          (row.recipientHashPrefix
                            ? `Hash ${row.recipientHashPrefix}…`
                            : 'Masked')}
                      </td>
                      <td className="max-w-xs px-4 py-4 text-gray-700">
                        {humanize(row.campaignKey || row.campaign_key)}
                      </td>
                      <td className="px-4 py-4 text-gray-700">{humanize(row.lane)}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-gray-600">
                        {formatDateTime(
                          row.queuedAt ||
                            row.queued_at ||
                            row.scheduledAt ||
                            row.dueAt ||
                            row.createdAt ||
                            row.created_at
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-gray-600">
                        {formatDateTime(
                          row.lastEvidenceAt ||
                            row.last_evidence_at ||
                            row.deliveredAt ||
                            row.sentAt ||
                            row.scheduledAt ||
                            row.updatedAt ||
                            row.updated_at
                        )}
                      </td>
                      <td className="max-w-48 truncate px-4 py-4 font-mono text-xs text-gray-600">
                        {row.providerMessageIdMasked ||
                          row.provider_message_id_masked ||
                          row.providerIdMasked ||
                          (row.owner ? humanize(row.owner) : 'Not exposed')}
                      </td>
                      <td className="max-w-xs px-4 py-4 text-gray-700">
                        {reason ? humanize(reason) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-gray-500">
            Showing {offset + 1}–{Math.min(offset + rows.length, total)} of {total}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={offset === 0}
              onClick={() => setOffset((value) => Math.max(0, value - PAGE_SIZE))}
              className={`min-h-[44px] rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 ${focusRing}`}
            >
              Previous
            </button>
            <button
              type="button"
              disabled={offset + rows.length >= total}
              onClick={() => setOffset((value) => value + PAGE_SIZE)}
              className={`min-h-[44px] rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 ${focusRing}`}
            >
              Next
            </button>
          </div>
        </div>
      </PageState>
    </div>
  );
}
